import React, { useState } from 'react';
import {
  DollarSign, FileText, Calendar, Package, Download, Eye,
  CreditCard, Clock, Check, AlertCircle, ChevronDown, ChevronUp,
  TrendingUp, ArrowRight, HelpCircle
} from 'lucide-react';

// Mock data - tenant's billing view
const tenantBillingInfo = {
  currentRate: 1.30, // Could be custom or global
  isCustomRate: false,
  globalRate: 1.30,
};

const currentWeekSummary = {
  weekRange: 'Feb 24 - Mar 2, 2026',
  packagesProcessed: 127,
  totalCharges: 165.10,
  invoiceDate: 'Monday, Mar 3, 2026',
  dueDate: 'Monday, Mar 10, 2026',
};

const billingHistory = [
  {
    id: 'INV-FS-2026-08',
    week: 'Feb 17 - Feb 23, 2026',
    packages: 142,
    rate: 1.30,
    amount: 184.60,
    status: 'paid',
    paidDate: 'Feb 26, 2026',
    paymentMethod: 'Bank Transfer',
  },
  {
    id: 'INV-FS-2026-07',
    week: 'Feb 10 - Feb 16, 2026',
    packages: 118,
    rate: 1.30,
    amount: 153.40,
    status: 'paid',
    paidDate: 'Feb 19, 2026',
    paymentMethod: 'Bank Transfer',
  },
  {
    id: 'INV-FS-2026-06',
    week: 'Feb 3 - Feb 9, 2026',
    packages: 135,
    rate: 1.30,
    amount: 175.50,
    status: 'paid',
    paidDate: 'Feb 12, 2026',
    paymentMethod: 'Bank Transfer',
  },
  {
    id: 'INV-FS-2026-05',
    week: 'Jan 27 - Feb 2, 2026',
    packages: 98,
    rate: 1.30,
    amount: 127.40,
    status: 'paid',
    paidDate: 'Feb 5, 2026',
    paymentMethod: 'Bank Transfer',
  },
];

const packageBreakdown = [
  { date: 'Mon, Feb 24', count: 18, amount: 23.40 },
  { date: 'Tue, Feb 25', count: 22, amount: 28.60 },
  { date: 'Wed, Feb 26', count: 15, amount: 19.50 },
  { date: 'Thu, Feb 27', count: 24, amount: 31.20 },
  { date: 'Fri, Feb 28', count: 19, amount: 24.70 },
  { date: 'Sat, Mar 1', count: 21, amount: 27.30 },
  { date: 'Sun, Mar 2', count: 8, amount: 10.40 },
];

export default function TenantBilling() {
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const totalPaid = billingHistory.filter(b => b.status === 'paid').reduce((sum, b) => sum + b.amount, 0);
  const totalPackages = billingHistory.reduce((sum, b) => sum + b.packages, 0);

  return (
    <div className="tenant-billing-page" data-testid="tenant-billing">
      {/* Header */}
      <div className="billing-page-header">
        <div className="header-content">
          <h2>Platform Processing Fees</h2>
          <p>Weekly charges for package processing services</p>
        </div>
        <button className="btn btn-secondary">
          <HelpCircle size={16} /> Billing FAQ
        </button>
      </div>

      {/* Current Rate Card */}
      <div className="rate-info-card">
        <div className="rate-display">
          <div className="rate-icon">
            <DollarSign size={28} />
          </div>
          <div className="rate-details">
            <span className="rate-label">Your Processing Rate</span>
            <div className="rate-amount">
              <span className="amount">${tenantBillingInfo.currentRate.toFixed(2)}</span>
              <span className="unit">USD per package</span>
            </div>
            {tenantBillingInfo.isCustomRate ? (
              <span className="rate-badge custom">Custom Negotiated Rate</span>
            ) : (
              <span className="rate-badge standard">Standard Rate</span>
            )}
          </div>
        </div>
        <div className="rate-info-text">
          <p>This fee is charged for each package processed through the Bolt Nexus platform. Invoices are generated weekly every Monday.</p>
        </div>
      </div>

      {/* Current Week Summary */}
      <div className="current-week-card">
        <div className="card-header">
          <div className="header-left">
            <Clock size={20} />
            <div>
              <h3>Current Week</h3>
              <span className="week-range">{currentWeekSummary.weekRange}</span>
            </div>
          </div>
          <span className="status-badge pending">Invoice Pending</span>
        </div>

        <div className="week-stats">
          <div className="stat-item">
            <Package size={20} />
            <div>
              <span className="stat-value">{currentWeekSummary.packagesProcessed}</span>
              <span className="stat-label">Packages Processed</span>
            </div>
          </div>
          <div className="stat-divider">×</div>
          <div className="stat-item">
            <DollarSign size={20} />
            <div>
              <span className="stat-value">${tenantBillingInfo.currentRate.toFixed(2)}</span>
              <span className="stat-label">Per Package</span>
            </div>
          </div>
          <div className="stat-divider">=</div>
          <div className="stat-item total">
            <CreditCard size={20} />
            <div>
              <span className="stat-value">${currentWeekSummary.totalCharges.toFixed(2)}</span>
              <span className="stat-label">Total Charges</span>
            </div>
          </div>
        </div>

        <div className="week-timeline">
          <div className="timeline-item">
            <Calendar size={14} />
            <span>Invoice will be generated on {currentWeekSummary.invoiceDate}</span>
          </div>
          <div className="timeline-item">
            <CreditCard size={14} />
            <span>Payment due by {currentWeekSummary.dueDate}</span>
          </div>
        </div>

        <button 
          className="breakdown-toggle"
          onClick={() => setShowBreakdown(!showBreakdown)}
          data-testid="toggle-breakdown"
        >
          {showBreakdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {showBreakdown ? 'Hide' : 'View'} Daily Breakdown
        </button>

        {showBreakdown && (
          <div className="daily-breakdown">
            <div className="breakdown-header">
              <span>Date</span>
              <span>Packages</span>
              <span>Charges</span>
            </div>
            {packageBreakdown.map((day, i) => (
              <div key={i} className="breakdown-row">
                <span className="date">{day.date}</span>
                <span className="packages">{day.count}</span>
                <span className="amount">${day.amount.toFixed(2)}</span>
              </div>
            ))}
            <div className="breakdown-total">
              <span>Week Total</span>
              <span>{currentWeekSummary.packagesProcessed}</span>
              <span>${currentWeekSummary.totalCharges.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="billing-stats-row">
        <div className="stat-card">
          <TrendingUp size={20} />
          <div>
            <span className="stat-value">${totalPaid.toFixed(2)}</span>
            <span className="stat-label">Total Paid (Last 4 Weeks)</span>
          </div>
        </div>
        <div className="stat-card">
          <Package size={20} />
          <div>
            <span className="stat-value">{totalPackages}</span>
            <span className="stat-label">Packages Processed</span>
          </div>
        </div>
        <div className="stat-card">
          <Check size={20} />
          <div>
            <span className="stat-value">{billingHistory.length}</span>
            <span className="stat-label">Invoices Paid</span>
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div className="billing-history-section">
        <div className="section-header">
          <h3>Billing History</h3>
          <button className="btn btn-ghost btn-sm" data-testid="export-history">
            <Download size={14} /> Export
          </button>
        </div>

        <div className="history-list">
          {billingHistory.map((invoice) => (
            <div 
              key={invoice.id} 
              className={`history-card ${expandedInvoice === invoice.id ? 'expanded' : ''}`}
              data-testid={`invoice-${invoice.id}`}
            >
              <div 
                className="history-card-main"
                onClick={() => setExpandedInvoice(expandedInvoice === invoice.id ? null : invoice.id)}
              >
                <div className="invoice-info">
                  <FileText size={18} />
                  <div>
                    <span className="invoice-id">{invoice.id}</span>
                    <span className="invoice-week">{invoice.week}</span>
                  </div>
                </div>

                <div className="invoice-packages">
                  <span className="value">{invoice.packages}</span>
                  <span className="label">packages</span>
                </div>

                <div className="invoice-rate">
                  <span className="value">${invoice.rate.toFixed(2)}</span>
                  <span className="label">/pkg</span>
                </div>

                <div className="invoice-amount">
                  <span className="value">${invoice.amount.toFixed(2)}</span>
                  <span className="label">USD</span>
                </div>

                <div className="invoice-status">
                  <span className={`status-badge ${invoice.status}`}>
                    {invoice.status === 'paid' ? (
                      <><Check size={12} /> Paid</>
                    ) : (
                      <><AlertCircle size={12} /> Pending</>
                    )}
                  </span>
                </div>

                <button className="expand-btn">
                  {expandedInvoice === invoice.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>

              {expandedInvoice === invoice.id && (
                <div className="history-card-expanded">
                  <div className="expanded-details">
                    <div className="detail-row">
                      <span className="label">Payment Date</span>
                      <span className="value">{invoice.paidDate}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Payment Method</span>
                      <span className="value">{invoice.paymentMethod}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Processing Rate</span>
                      <span className="value">${invoice.rate.toFixed(2)} per package</span>
                    </div>
                  </div>
                  <div className="expanded-actions">
                    <button className="btn btn-ghost btn-sm">
                      <Eye size={14} /> View Invoice
                    </button>
                    <button className="btn btn-ghost btn-sm">
                      <Download size={14} /> Download PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Payment Info */}
      <div className="payment-info-card">
        <div className="info-header">
          <CreditCard size={20} />
          <h3>Payment Information</h3>
        </div>
        <div className="info-content">
          <p>Processing fee invoices are generated every Monday and are due within 7 days. Payment can be made via bank transfer to:</p>
          <div className="bank-details">
            <div className="detail-row">
              <span className="label">Bank Name</span>
              <span className="value">National Commercial Bank Jamaica</span>
            </div>
            <div className="detail-row">
              <span className="label">Account Name</span>
              <span className="value">Bolt Nexus Ltd.</span>
            </div>
            <div className="detail-row">
              <span className="label">Account Number</span>
              <span className="value mono">3847-2938-4756</span>
            </div>
            <div className="detail-row">
              <span className="label">Reference</span>
              <span className="value mono">Your Tenant ID (e.g., FS-001)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
